import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from "react-bootstrap";
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_URL } from '../../../api/api';

const API_PRODUCTS = `${API_URL}/products`;
const API_CATEGORIES = `${API_URL}/product-categories`;

const ManageProductLayer = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        name: '',
        category_id: '',
        regular_price: '',
        sale_price: '',
        gst: '',
        status: 'active'
    });
    const [editId, setEditId] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 5;

    const token = localStorage.getItem('token');
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const fetchProducts = () => {
        const params = {
            search: searchTerm,
            category_id: filterCategory,
            page: currentPage,
            per_page: itemsPerPage
        };

        axios.get(API_PRODUCTS, { params })
            .then(res => {
                setProducts(res.data.data);
                setTotalPages(res.data.last_page);
            })
            .catch(err => console.error('Error fetching products:', err));
    };

    const fetchCategories = () => {
        axios.get(API_CATEGORIES)
            .then(res => setCategories(res.data))
            .catch(err => console.error('Error fetching categories:', err));
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [searchTerm, filterCategory, currentPage]);

    const handleShowModal = (product = null) => {
        if (product) {
            setEditId(product.id);
            setForm({
                name: product.name || '',
                category_id: product.category_id || '',
                regular_price: product.regular_price || '',
                sale_price: product.sale_price || '',
                gst: product.gst || '',
                status: product.status || 'active'
            });
        } else {
            setEditId(null);
            setForm({
                name: '',
                category_id: '',
                regular_price: '',
                sale_price: '',
                gst: '',
                status: 'active'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditId(null);
        setForm({
            name: '',
            category_id: '',
            regular_price: '',
            sale_price: '',
            gst: '',
            status: 'active'
        });
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            Swal.fire("Validation Error", "Product name is required", "error");
            return;
        }
        if (!form.category_id) {
            Swal.fire("Validation Error", "Category is required", "error");
            return;
        }
        if (!form.regular_price) {
            Swal.fire("Validation Error", "Regular price is required", "error");
            return;
        }
        if (!form.gst) {
            Swal.fire("Validation Error", "GST is required", "error");
            return;
        }

        try {
            if (editId) {
                await axios.put(`${API_PRODUCTS}/${editId}`, form);
                Swal.fire("Updated", "Product updated successfully", "success");
            } else {
                await axios.post(API_PRODUCTS, form);
                Swal.fire("Added", "Product added successfully", "success");
            }
            handleCloseModal();
            fetchProducts();
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Operation failed", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "This will delete the product.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!"
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${API_PRODUCTS}/${id}`);
                Swal.fire("Deleted!", "Product has been deleted.", "success");
                fetchProducts();
            } catch (err) {
                console.error(err);
                Swal.fire("Error", "Delete failed", "error");
            }
        }
    };

    return (
        <div className="row gy-4">
            <div className="col-md-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Manage Products</h6>
                        <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
                            <i className="ri-add-line" /> Add Product
                        </Button>
                    </div>
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search by name"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{ maxWidth: '250px' }}
                            />
                            <Form.Select
                                value={filterCategory}
                                onChange={(e) => {
                                    setFilterCategory(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{ maxWidth: '200px' }}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <Table bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Product Name</th>
                                    <th>Category Name</th>
                                    <th>Regular Price</th>
                                    <th>Sale Price</th>
                                    <th>GST</th>
                                    <th>Status</th>
                                    <th style={{ width: "150px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.length > 0 ? (
                                    products.map((product, index) => (
                                        <tr key={product.id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td>{product.name}</td>
                                            <td>{product.category?.name || '-'}</td>
                                            <td>{product.regular_price}</td>
                                            <td>{product.sale_price}</td>
                                            <td>{product.gst}</td>
                                            <td>{product.status}</td>
                                            <td>
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleShowModal(product)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(product.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center">No products found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                variant="secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="me-2"
                            >
                                Previous
                            </Button>
                            <span className="align-self-center">Page {currentPage} of {totalPages}</span>
                            <Button
                                variant="secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="ms-2"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <h6>{editId ? 'Edit' : 'Add'} Product</h6>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder="Enter product name"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select
                                name="category_id"
                                value={form.category_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">-- Select Category --</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Regular Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="regular_price"
                                value={form.regular_price}
                                onChange={handleChange}
                                placeholder="Enter regular price"
                                required
                                min="0"
                                step="0.01"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Sale Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="sale_price"
                                value={form.sale_price}
                                onChange={handleChange}
                                placeholder="Enter sale price (optional)"
                                min="0"
                                step="0.01"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>GST (%)</Form.Label>
                            <Form.Control
                                type="number"
                                name="gst"
                                value={form.gst}
                                onChange={handleChange}
                                placeholder="Enter GST percentage"
                                required
                                min="0"
                                step="0.01"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                                required
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="text-end">
                            <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                {editId ? 'Update' : 'Add'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ManageProductLayer;
